import { DateTime } from 'luxon';
import { CoachHistoryRowView } from '@cf-shared/coach-history-interface';
import { CoachHistoryKind, CoachHistoryRoute } from '@cf-shared/data-model/coach-history';

// The pure parts of the coach billing page's Past Payments card (#6768): which
// charges it lists, which receipts lines it adds, and how an amount is written.
// The rules: design-docs/coach-billing-analytics.md, "Phase 3 — the coach's view".

/** How many charges the folded card shows. With this many or fewer, the card does not fold. */
export const FOLDED_CHARGE_COUNT = 3;

/** The months of charges the card lists. */
const CHARGE_MONTHS = 12;

/** Every charge whose time falls within the last 12 months in the viewer's zone,
 *  newest first. The server sends a little more history, for the chart only. */
export function chargesForPastPayments(rows: readonly CoachHistoryRowView[], now: DateTime): CoachHistoryRowView[] {
  const cutoffMs = now.minus({ months: CHARGE_MONTHS }).toMillis();
  return rows
    .filter(row => row.kind === CoachHistoryKind.Charge)
    .filter(row => DateTime.fromISO(row.at_utc).toMillis() >= cutoffMs)
    .sort((a, b) => DateTime.fromISO(b.at_utc).toMillis() - DateTime.fromISO(a.at_utc).toMillis());
}

/** The stores whose charges appear in the list, each once, App Store first, for the receipts lines. */
export function storesInCharges(charges: readonly CoachHistoryRowView[]): (CoachHistoryRoute.Apple | CoachHistoryRoute.GooglePlay)[] {
  const routes = new Set(charges.map(charge => charge.paymentRoute));
  const stores: (CoachHistoryRoute.Apple | CoachHistoryRoute.GooglePlay)[] = [];
  if (routes.has(CoachHistoryRoute.Apple)) stores.push(CoachHistoryRoute.Apple);
  if (routes.has(CoachHistoryRoute.GooglePlay)) stores.push(CoachHistoryRoute.GooglePlay);
  return stores;
}

/**
 * A charge amount as money, from the MAJOR units Phase 2 stores (29.97, not
 * 2997). `formatMinorCurrencyAmount` takes minor units and would show €0.30, so
 * it must not be used here. Throws a RangeError for a currency code the browser
 * does not know; the page reports that and shows the stored figures instead.
 */
export function formatMajorCurrencyAmount(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}
